/**
 * Plain-text diagnostic report meant to be PASTED as-is.
 *
 * Born from a concrete case: the app stayed "syncing" forever and the
 * reason was that the PowerSync service was not even up. From the
 * screen there was no way to tell "does not connect" from "connects but is slow", and
 * describing it by hand loses half the data.
 *
 * SINGLE SOURCE. The first version received its own object while the screen
 * painted its own rows: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now
 * both consume the SAME sections array: the screen renders it, this
 * function serializes it and the clue is computed by reading it.
 *
 * Each element carries three fields for distinct reasons:
 *   · `label`/`value` — already-translated text: what is seen and what is copied.
 *   · `status`        — only used by the UI for color; the report ignores it.
 *   · `raw`           — UNtranslated value, so logic does not depend on the
 *                       language. Without it the clue would stop firing as soon as
 *                       someone changed a translation.
 *
 * NO personal data: no email, no name, no token. Whoever builds
 * the sections is responsible for redacting identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable, language-independent identifier. Used by the logic. */
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated / formatted value, as seen on screen. */
  value: string;
  /** UI only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on the language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. Passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Looks up by `key`, never by label: labels are translated. */
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable clue for the pattern that started all of this: there is a session and
 * sync never starts. It is almost always the service being down, not the app, and
 * saying so here saves searching in the wrong place.
 */
function hint(sections: DiagnosticSection[]): string[] {
  const session = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    session?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    parts.push("", `## ${section.title}`);
    for (const item of section.items) {
      // An empty field is MARKED rather than omitted: in a pasted report, an
      // absent datum and one not collected read the same unless you say which is which.
      parts.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  parts.push(...hint(input.sections));
  return parts.join("\n");
}
